import { useEffect, useState } from 'react'
import App from './App.jsx'
import TermsModal from './TermsModal.jsx'
import { BotAvatar } from './components/Avatars.jsx'
import './widget.css'

const TERMS_KEY = 'allieChatTermsAccepted'
const SEEN_KEY = 'allieChatBubbleSeen'
const GREETING_DELAY_MS = 4000

function readFlag(key) {
  try {
    return window.localStorage.getItem(key) === '1'
  } catch {
    // Privacy mode / storage disabled: fall back to the "not yet seen /
    // not yet accepted" default rather than crashing the widget.
    return false
  }
}

function writeFlag(key) {
  try {
    window.localStorage.setItem(key, '1')
  } catch {
    // Ignore write failures; worst case the flag doesn't stick.
  }
}

function hasAcceptedTerms() {
  return readFlag(TERMS_KEY)
}

function rememberTermsAccepted() {
  writeFlag(TERMS_KEY)
}

function getConfig() {
  if (typeof window === 'undefined') return {}
  return window.AllieChatConfig || {}
}

export default function WidgetApp() {
  const [open, setOpen] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [hasBeenSeen, setHasBeenSeen] = useState(() => readFlag(SEEN_KEY))
  const [showGreeting, setShowGreeting] = useState(false)

  const attentionEffect = getConfig().attentionEffect || 'none'
  const greetingText = getConfig().greetingText || 'Need help? Ask Allie!'

  // Attention effects run once per visitor: they stop for good as soon as
  // the bubble has been opened or the greeting dismissed, so a returning
  // visitor isn't nagged by the same animation on every page.
  useEffect(() => {
    if (attentionEffect !== 'greeting' || hasBeenSeen) return
    const timer = setTimeout(() => setShowGreeting(true), GREETING_DELAY_MS)
    return () => clearTimeout(timer)
  }, [hasBeenSeen, attentionEffect])

  const markSeen = () => {
    if (!hasBeenSeen) {
      writeFlag(SEEN_KEY)
      setHasBeenSeen(true)
    }
    setShowGreeting(false)
  }

  const handleToggle = () => {
    if (open) {
      setOpen(false)
      return
    }
    markSeen()
    if (!hasAcceptedTerms()) {
      setShowTerms(true)
    }
    setOpen(true)
  }

  const handleAcceptTerms = () => {
    rememberTermsAccepted()
    setShowTerms(false)
  }

  const handleCancelTerms = () => {
    setShowTerms(false)
    setOpen(false)
  }

  const activeEffect = !open && !hasBeenSeen ? attentionEffect : 'none'
  const bubbleClassName =
    'allie-widget-bubble' +
    (activeEffect === 'pulse' ? ' allie-effect-pulse' : '') +
    (activeEffect === 'bounce' ? ' allie-effect-bounce' : '')

  return (
    <div className="allie-widget-root">
      {open && (
        <div className="allie-widget-panel" role="dialog" aria-label="Allie chat">
          {showTerms ? (
            <TermsModal onAccept={handleAcceptTerms} onCancel={handleCancelTerms} />
          ) : (
            <App onBack={() => setOpen(false)} />
          )}
        </div>
      )}

      {activeEffect === 'greeting' && showGreeting && (
        <div className="allie-greeting-bubble" role="status" onClick={handleToggle}>
          <button
            type="button"
            className="allie-greeting-close"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation()
              markSeen()
            }}
          >
            ✕
          </button>
          {greetingText}
        </div>
      )}

      <button
        type="button"
        className={bubbleClassName}
        aria-label={open ? 'Close chat' : 'Open Allie chat'}
        onClick={handleToggle}
      >
        {open ? '✕' : <BotAvatar size={60} />}
        {activeEffect === 'badge' && <span className="allie-widget-badge" aria-hidden="true" />}
      </button>
    </div>
  )
}
